import type { ComponentPropsWithoutRef, ReactNode } from 'react'
import * as s from './controls.css'

function cx(...parts: (string | false | undefined)[]) {
  return parts.filter(Boolean).join(' ')
}

export function Button({
  variant,
  size,
  block,
  className,
  type = 'button',
  ...rest
}: ComponentPropsWithoutRef<'button'> & {
  variant?: 'primary' | 'ghost' | 'quiet' | 'danger'
  size?: 'sm' | 'md' | 'lg'
  block?: boolean
}) {
  return <button type={type} className={cx(s.button({ variant, size, block }), className)} {...rest} />
}

export type SegOption<T> = { value: T; label: ReactNode; title?: string }
// A group of mutually exclusive options: the period on Live and on Stats, the Directory's Show
// and Sort, the account's language. `role="group"` + aria-pressed rather than a tablist —
// nothing here reveals a tab panel, and a reader told "tab" then finding no panel is misled.
export function SegmentedControl<T extends string | number>({
  options,
  value,
  onChange,
  ariaLabel,
  className,
}: {
  options: SegOption<T>[]
  value: T
  onChange: (value: T) => void
  ariaLabel?: string
  className?: string
}) {
  return (
    <div role="group" aria-label={ariaLabel} className={cx(s.segTrack, className)}>
      {options.map((o) => {
        const active = o.value === value
        return (
          <button
            key={String(o.value)}
            type="button"
            aria-pressed={active}
            title={o.title}
            className={s.segItem({ active })}
            onClick={() => onChange(o.value)}
          >
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

export function Field({ className, ...rest }: ComponentPropsWithoutRef<'input'>) {
  return <input className={cx(s.field, className)} {...rest} />
}

export function Select({ className, children, ...rest }: ComponentPropsWithoutRef<'select'>) {
  return (
    <select className={cx(s.field, className)} {...rest}>
      {children}
    </select>
  )
}

export function Textarea({ className, ...rest }: ComponentPropsWithoutRef<'textarea'>) {
  return <textarea className={cx(s.textarea, className)} {...rest} />
}

export function Checkbox({ className, ...rest }: ComponentPropsWithoutRef<'input'>) {
  return <input type="checkbox" className={cx(s.checkbox.input, className)} {...rest} />
}

// The close mark on an overlay or drawer.
export function CloseButton({ className, ...rest }: ComponentPropsWithoutRef<'button'>) {
  return (
    <button type="button" className={cx(s.close, className)} {...rest}>
      ×
    </button>
  )
}

// A control that is only text, in the utility voice.
export function QuietButton({ className, ...rest }: ComponentPropsWithoutRef<'button'>) {
  return <button type="button" className={cx(s.quietText, className)} {...rest} />
}
